import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster, ToastBar, toast } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { EstablishmentProvider } from './contexts/EstablishmentContext';
import { AlertDialogProvider } from './contexts/AlertDialogContext';
import { ConfirmDialogProvider } from './contexts/ConfirmDialogContext';
import { PromptDialogProvider } from './contexts/PromptDialogContext';
import { useAuth } from './hooks/useAuth';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import { PageLoader } from './components/ui/PageLoader';
import { preventiveMaintenanceRoutes } from './routes/preventiveMaintenanceRoutes';
import * as Sentry from '@sentry/react';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import LoginPage from './pages/auth/LoginPage';
import MainLayout from './components/layout/MainLayout';

// Lazy load all other pages for better performance
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const CadastroPage = lazy(() => import('./pages/cadastro/CadastroPage'));
const SyndicAgendaPage = lazy(() => import('./pages/agenda/SyndicAgendaPage'));
const ServiceProviderAgendaPage = lazy(() => import('./pages/prestador/ServiceProviderAgendaPage'));
const AreaReservationsPage = lazy(() => import('./pages/agenda/AreaReservationsPage'));
const SuportePage = lazy(() => import('./pages/suporte/SuportePage'));
const ReclamacoesPage = lazy(() => import('./pages/reclamacoes/ReclamacoesPage'));
const CorrectiveMaintenanceCondominioPage = lazy(() => import('./pages/manutencao-corretiva/CorrectiveMaintenanceCondominioPage'));
const CorrectiveMaintenanceMoradorPage = lazy(() => import('./pages/manutencao-corretiva/CorrectiveMaintenanceMoradorPage'));
const CentralSolicitacoesPage = lazy(() => import('./pages/central-de-solicitacoes/CentralSolicitacoesPage'));
const AssembleiasPage = lazy(() => import('./pages/assembleias/AssembleiasPage'));
const DocumentosPage = lazy(() => import('./pages/documentos/DocumentosPage'));
const ComunicacoesPage = lazy(() => import('./pages/comunicacoes/ComunicacoesPage'));
const AssistenciasPage = lazy(() => import('./pages/assistencias/AssistenciasPage'));
const PertencesPage = lazy(() => import('./pages/pertences/PertencesPage'));
const ResidentAnnouncementsPage = lazy(() => import('./pages/social/ResidentAnnouncementsPage'));
const CommerceOffersPage = lazy(() => import('./pages/social/CommerceOffersPage'));
const SugestoesPage = lazy(() => import('./pages/sugestoes/SugestoesPage'));
const PatrimoniosPage = lazy(() => import('./pages/patrimonios/PatrimoniosPage'));
// const InadimplenciaPage = lazy(() => import('./pages/inadimplencia/InadimplenciaPage'));
// const PaymentAgreementDetails = lazy(() => import('./pages/inadimplencia/PaymentAgreementDetails'));
const RotinasDoZeladorPage = lazy(() => import('./pages/zelador/RotinasDoZeladorPage'));
const ForbiddenPage = lazy(() => import('./pages/status/ForbiddenPage'));
const NotFoundPage = lazy(() => import('./pages/status/NotFoundPage'));
const ConfiguracaoCondominioPage = lazy(() => import('./pages/configuracoes/ConfiguracaoCondominioPage'));
const MeusDadosPage = lazy(() => import('./pages/configuracoes/MeusDadosPage'));
const PermissoesAcessoPage = lazy(() => import('./pages/configuracoes/PermissoesAcessoPage'));
const NotificacoesPreferenciasPage = lazy(() => import('./pages/configuracoes/NotificacoesPreferenciasPage'));
const PrestadoresServicosPage = lazy(() => import('./pages/configuracoes/prestadores-servicos'));
const CommonAreasPage = lazy(() => import('./pages/configuracoes/areas-comuns'));

// Condominio Module
const CondominioListPage = lazy(() => import('./pages/condominio/CondominioListPage'));
const CondominioDetailsPage = lazy(() => import('./pages/condominio/CondominioDetailsPage'));
const CondominioCreatePage = lazy(() => import('./pages/condominio/CondominioCreatePage'));
const CondominioEditPage = lazy(() => import('./pages/condominio/CondominioEditPage'));

// EAD Module
const CourseListingPage = lazy(() => import('./pages/ead/CourseListingPage'));
const CourseDetailsPage = lazy(() => import('./pages/ead/CourseDetailsPage'));
const LessonPage = lazy(() => import('./pages/ead/LessonPage'));

// Governance Module
const VotingPage = lazy(() => import('./pages/governanca/VotingPage'));
const GovernanceDocumentosPage = lazy(
  () => import('./pages/governanca/GovernanceDocumentosPage'),
);
const GovernanceMaintenanceHistoryPage = lazy(
  () => import('./pages/governanca/GovernanceMaintenanceHistoryPage'),
);
const GovernancePrestadoresPage = lazy(
  () => import('./pages/governanca/GovernancePrestadoresPage'),
);
const GovernanceRotinasZeladorPage = lazy(
  () => import('./pages/governanca/GovernanceRotinasZeladorPage'),
);
const GovernanceComunicacoesPage = lazy(
  () => import('./pages/governanca/GovernanceComunicacoesPage'),
);
const GovernanceEficienciaPage = lazy(
  () => import('./pages/governanca/GovernanceEficienciaPage'),
);
const GovernanceSocialPage = lazy(
  () => import('./pages/governanca/GovernanceSocialPage'),
);
const GovernancePortariaPage = lazy(
  () => import('./pages/governanca/GovernancePortariaPage'),
);
const ResidentGovernancePage = lazy(
  () => import('./pages/governanca/ResidentGovernancePage'),
);

// Access Control Module (Segware)
const AccessControlDashboardPage = lazy(() => import('./pages/controle-acesso/AccessControlDashboardPage'));
const AccessControlEventsPage = lazy(() => import('./pages/controle-acesso/AccessControlEventsPage'));
const AccessControlDoorsPage = lazy(() => import('./pages/controle-acesso/AccessControlDoorsPage'));
const AccessControlConfigPage = lazy(() => import('./pages/controle-acesso/AccessControlConfigPage'));
const AccessControlIndexRedirect = lazy(() => import('./pages/controle-acesso/AccessControlIndexRedirect'));
const AccessControlPeoplePage = lazy(() => import('./pages/controle-acesso/AccessControlPeoplePage'));
const AccessControlPersonFormPage = lazy(() => import('./pages/controle-acesso/AccessControlPersonFormPage'));

// Entry Control Module
const QuickCheckInPage = lazy(() => import('./pages/portaria/QuickCheckInPage'));
const VisitorManagementPage = lazy(() => import('./pages/portaria/VisitorManagementPage'));
const DeliveryManagementPage = lazy(() => import('./pages/portaria/DeliveryManagementPage'));
const SecurityAlertsPage = lazy(() => import('./pages/portaria/SecurityAlertsPage'));
const EmergencyPlanPage = lazy(() => import('./pages/portaria/EmergencyPlanPage'));
const LostAndFoundPage = lazy(() => import('./pages/portaria/lost-and-found/LostAndFoundPage').then(m => ({ default: m.LostAndFoundPage })));
const CheckinReportPage = lazy(() => import('./pages/portaria/CheckinReportPage'));

// Constructor Module

// Gestão de Condomínios Module (syndic view)
const GestaoCondominiosPage = lazy(() => import('./pages/gestao-condominios/GestaoCondominiosPage'));
const SyndicCondominiosPage = lazy(() => import('./pages/gestao-condominios/SyndicCondominiosPage'));

// Configurações Administrativas Module (Admin MEP only)
const AdminConfiguracoesPage = lazy(() => import('./pages/configuracoes-administrativas/AdminConfiguracoesPage'));
const AdminGestaoCondominiosPage = lazy(() => import('./pages/configuracoes-administrativas/AdminGestaoCondominiosPage'));
const AdminGestaoUsuariosPage = lazy(() => import('./pages/configuracoes-administrativas/AdminGestaoUsuariosPage'));
const AdminGovernancaGlobalPage = lazy(() => import('./pages/configuracoes-administrativas/AdminGovernancaGlobalPage'));

// Style Guide
const StyleGuidePage = lazy(() => import('./pages/style-guide/StyleGuidePage'));

// Public pages
const SetPasswordPage = lazy(() => import('./pages/auth/SetPasswordPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const PublicCommerceSignupPage = lazy(
  () => import('./pages/public/PublicCommerceSignupPage'),
);
const PublicCommerceManagePage = lazy(
  () => import('./pages/public/PublicCommerceManagePage'),
);

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Suspense wrapper for lazy loaded routes
const SuspenseRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Suspense fallback={<PageLoader />}>
      {children}
    </Suspense>
  );
};

// App with AuthProvider - handles authenticated routes and login
const AppWithAuth: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<LoginPage />} />
      <Route path="/setup" element={<Navigate to="/configuracoes/condominio" replace />} />
      <Route path="/" element={
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      }>
        <Route path="/dashboard" element={<SuspenseRoute><DashboardPage /></SuspenseRoute>} />
        <Route path="/403" element={<SuspenseRoute><ForbiddenPage /></SuspenseRoute>} />
        
        {/* Manutenção Preventiva — rotas e gate de permissão em routes/preventiveMaintenanceRoutes */}
        {preventiveMaintenanceRoutes()}

        {/* Manutenção Corretiva Routes */}
        <Route path="/manutencao-corretiva" element={<Navigate to="/manutencao-corretiva/condominio" replace />} />
        <Route path="/manutencao-corretiva/condominio" element={<SuspenseRoute><CorrectiveMaintenanceCondominioPage /></SuspenseRoute>} />
        <Route path="/manutencao-corretiva/morador" element={<SuspenseRoute><CorrectiveMaintenanceMoradorPage /></SuspenseRoute>} />

        {/* Central de Solicitações (WhatsApp) */}
        <Route path="/central-de-solicitacoes" element={<SuspenseRoute><CentralSolicitacoesPage /></SuspenseRoute>} />


        {/* Agenda do Síndico - Top Level */}
        <Route path="/agenda-sindico" element={<SuspenseRoute><SyndicAgendaPage /></SuspenseRoute>} />

        {/* Agenda do Prestador - Top Level */}
        <Route path="/prestador/agenda" element={<SuspenseRoute><ServiceProviderAgendaPage /></SuspenseRoute>} />

        {/* Social Routes */}
        <Route path="/social" element={<Navigate to="/social/assembleias" replace />} />
        <Route path="/social/agenda/reservas" element={<Navigate to="/reservas/reserva-de-areas" replace />} />
        <Route path="/social/suporte-tecnico" element={<SuspenseRoute><SuportePage /></SuspenseRoute>} />
        <Route path="/social/reclamacoes---sugestoes" element={<SuspenseRoute><ReclamacoesPage /></SuspenseRoute>} />
        <Route path="/social/assembleias" element={<SuspenseRoute><AssembleiasPage /></SuspenseRoute>} />
        <Route path="/social/assembleias-virtuais" element={<Navigate to="/social/assembleias" replace />} />
        <Route path="/social/meus-pertences" element={<SuspenseRoute><PertencesPage /></SuspenseRoute>} />
        <Route path="/social/anuncios-condominos" element={<SuspenseRoute><ResidentAnnouncementsPage /></SuspenseRoute>} />
        <Route path="/social/anuncios-comercio-local" element={<SuspenseRoute><CommerceOffersPage /></SuspenseRoute>} />
        <Route path="/social/sugestoes" element={<SuspenseRoute><SugestoesPage /></SuspenseRoute>} />

        {/* Portaria e Segurança Routes */}
        <Route path="/portaria-e-seguranca" element={<Navigate to="/portaria-e-seguranca/check-in-rapido" replace />} />
        <Route path="/portaria-e-seguranca/check-in-rapido" element={<SuspenseRoute><QuickCheckInPage /></SuspenseRoute>} />
        <Route path="/portaria-e-seguranca/check-in-out" element={<SuspenseRoute><VisitorManagementPage /></SuspenseRoute>} />
        <Route path="/portaria-e-seguranca/controle-de-encomendas" element={<SuspenseRoute><DeliveryManagementPage /></SuspenseRoute>} />
        <Route path="/portaria-e-seguranca/cadastro-de-visitantes" element={<SuspenseRoute><VisitorManagementPage /></SuspenseRoute>} />
        <Route path="/portaria-e-seguranca/ocorrencias-de-seguranca" element={<SuspenseRoute><SecurityAlertsPage /></SuspenseRoute>} />
        <Route path="/portaria-e-seguranca/patrimonios" element={<SuspenseRoute><PatrimoniosPage /></SuspenseRoute>} />
        <Route path="/portaria-e-seguranca/achados-e-perdidos" element={<SuspenseRoute><LostAndFoundPage /></SuspenseRoute>} />
        <Route path="/portaria-e-seguranca/relatorios-checkin" element={<SuspenseRoute><CheckinReportPage /></SuspenseRoute>} />
        <Route path="/portaria-e-seguranca/ensaio-de-emergencia" element={<SuspenseRoute><EmergencyPlanPage /></SuspenseRoute>} />

        {/* Controle de Acesso Routes (Segware) */}
        <Route path="/controle-de-acesso" element={<SuspenseRoute><AccessControlIndexRedirect /></SuspenseRoute>} />
        <Route path="/controle-de-acesso/dashboard" element={<SuspenseRoute><AccessControlDashboardPage /></SuspenseRoute>} />
        <Route path="/controle-de-acesso/eventos" element={<SuspenseRoute><AccessControlEventsPage /></SuspenseRoute>} />
        <Route path="/controle-de-acesso/moradores" element={<Navigate to="/configuracoes/cadastros-de-usuario" replace />} />
        <Route path="/controle-de-acesso/portas" element={<SuspenseRoute><AccessControlDoorsPage /></SuspenseRoute>} />
        <Route path="/controle-de-acesso/configuracao" element={<SuspenseRoute><AccessControlConfigPage /></SuspenseRoute>} />
        <Route path="/controle-de-acesso/pessoas" element={<SuspenseRoute><AccessControlPeoplePage /></SuspenseRoute>} />
        <Route path="/controle-de-acesso/pessoas/nova" element={<SuspenseRoute><AccessControlPersonFormPage /></SuspenseRoute>} />

        {/* Prestadores de Serviços Routes */}
        <Route path="/prestadores-de-servicos" element={<Navigate to="/prestadores-de-servicos/condominio" replace />} />
        <Route path="/prestadores-de-servicos/condominio" element={<SuspenseRoute><PrestadoresServicosPage /></SuspenseRoute>} />
        
        <Route path="/prestadores-de-servicos/condomino" element={<Navigate to="/prestadores-de-servicos/condomino/assitencias-tecnicas" replace />} />
        <Route path="/prestadores-de-servicos/condomino/assitencias-tecnicas" element={<SuspenseRoute><AssistenciasPage /></SuspenseRoute>} />
        <Route path="/prestadores-de-servicos/condomino/aulas" element={<SuspenseRoute><AssistenciasPage /></SuspenseRoute>} />
        <Route path="/prestadores-de-servicos/condomino/automoveis" element={<SuspenseRoute><AssistenciasPage /></SuspenseRoute>} />
        <Route path="/prestadores-de-servicos/condomino/consultoria" element={<SuspenseRoute><AssistenciasPage /></SuspenseRoute>} />
        <Route path="/prestadores-de-servicos/condomino/design-e-tecnologia" element={<SuspenseRoute><AssistenciasPage /></SuspenseRoute>} />
        <Route path="/prestadores-de-servicos/condomino/eventos" element={<SuspenseRoute><AssistenciasPage /></SuspenseRoute>} />
        <Route path="/prestadores-de-servicos/condomino/moda-e-beleza" element={<SuspenseRoute><AssistenciasPage /></SuspenseRoute>} />
        <Route path="/prestadores-de-servicos/condomino/reformas-e-reparos" element={<SuspenseRoute><AssistenciasPage /></SuspenseRoute>} />
        <Route path="/prestadores-de-servicos/condomino/saude" element={<SuspenseRoute><AssistenciasPage /></SuspenseRoute>} />
        <Route path="/prestadores-de-servicos/condomino/servicos-domesticos" element={<SuspenseRoute><AssistenciasPage /></SuspenseRoute>} />

        {/* Governança Routes */}
        <Route path="/governanca" element={<Navigate to="/governanca/manutencoes" replace />} />
        <Route path="/governanca/manutencoes" element={<SuspenseRoute><GovernanceMaintenanceHistoryPage /></SuspenseRoute>} />
        <Route path="/governanca/prestadores-de-servico" element={<SuspenseRoute><GovernancePrestadoresPage /></SuspenseRoute>} />
        <Route path="/governanca/rotinas-do-zelador" element={<SuspenseRoute><GovernanceRotinasZeladorPage /></SuspenseRoute>} />
        <Route path="/governanca/comunicacoes" element={<SuspenseRoute><GovernanceComunicacoesPage /></SuspenseRoute>} />
        <Route path="/governanca/eficiencia-operacional" element={<SuspenseRoute><GovernanceEficienciaPage /></SuspenseRoute>} />
        <Route path="/governanca/social" element={<SuspenseRoute><GovernanceSocialPage /></SuspenseRoute>} />
        <Route path="/governanca/governanca-global" element={<SuspenseRoute><AdminGovernancaGlobalPage /></SuspenseRoute>} />
        <Route path="/governanca/documentos" element={<SuspenseRoute><GovernanceDocumentosPage /></SuspenseRoute>} />
        <Route path="/governanca/portaria-e-seguranca" element={<SuspenseRoute><GovernancePortariaPage /></SuspenseRoute>} />
        {/* Visão read-only do morador */}
        <Route path="/governanca/condomino" element={<SuspenseRoute><ResidentGovernancePage /></SuspenseRoute>} />
        {/* Backward compatibility */}
        <Route path="/governanca/governanca-de-manutencoes" element={<Navigate to="/governanca/manutencoes" replace />} />

        {/* Comunicações Routes */}
        <Route path="/comunicacoes" element={<Navigate to="/comunicacoes/comunicados-e-avisos" replace />} />
        <Route path="/comunicacoes/comunicados-e-avisos" element={<SuspenseRoute><ComunicacoesPage /></SuspenseRoute>} />
        <Route path="/comunicacoes/enquetes" element={<SuspenseRoute><VotingPage /></SuspenseRoute>} />
        <Route path="/comunicacoes/enquetes-e-votacoes" element={<Navigate to="/comunicacoes/enquetes" replace />} />
        <Route path="/comunicacoes/assembleias-virtuais" element={<Navigate to="/social/assembleias" replace />} />

        {/* Rotinas do Zelador */}
        <Route path="/rotinas-do-zelador" element={<SuspenseRoute><RotinasDoZeladorPage /></SuspenseRoute>} />

        {/* Reservas Routes */}
        <Route path="/reservas" element={<Navigate to="/reservas/reserva-de-areas" replace />} />
        <Route path="/reservas/reserva-de-areas" element={<SuspenseRoute><AreaReservationsPage /></SuspenseRoute>} />
        <Route path="/reservas/areas-comuns" element={<SuspenseRoute><CommonAreasPage /></SuspenseRoute>} />
        <Route path="/reservas/historico-de-reservas" element={<Navigate to="/reservas/reserva-de-areas" replace />} />
        <Route path="/reservas/regras-de-uso" element={<Navigate to="/reservas/reserva-de-areas" replace />} />

        {/* Documentos Routes */}
        <Route path="/documentos" element={<Navigate to="/documentos/regulamentos-internos" replace />} />
        <Route path="/documentos/regulamentos-internos" element={<SuspenseRoute><DocumentosPage /></SuspenseRoute>} />
        <Route path="/documentos/atas-de-reunioes" element={<SuspenseRoute><DocumentosPage /></SuspenseRoute>} />
        <Route path="/documentos/contratos" element={<SuspenseRoute><DocumentosPage /></SuspenseRoute>} />
        <Route path="/documentos/convencao-do-condominio" element={<SuspenseRoute><DocumentosPage /></SuspenseRoute>} />
        <Route path="/documentos/regras-de-utilizacao-de-areas-comuns" element={<SuspenseRoute><DocumentosPage /></SuspenseRoute>} />
        <Route path="/documentos/normas-de-seguranca" element={<SuspenseRoute><DocumentosPage /></SuspenseRoute>} />
        <Route path="/documentos/inscricao-do-edificio-na-receita-federal-com-o-cartao-cnpj-atualizado" element={<SuspenseRoute><DocumentosPage /></SuspenseRoute>} />
        <Route path="/documentos/lei-do-condominio" element={<SuspenseRoute><DocumentosPage /></SuspenseRoute>} />
        <Route path="/documentos/politica-de-privacidade-do-condominio" element={<SuspenseRoute><DocumentosPage /></SuspenseRoute>} />
        <Route path="/documentos/planta-baixa-da-area-contruida-1-copia-em-papel-impresso-1-copia-digital" element={<SuspenseRoute><DocumentosPage /></SuspenseRoute>} />

        {/* Configurações Routes */}
        <Route path="/configuracoes" element={<Navigate to="/configuracoes/meus-dados" replace />} />
        <Route path="/configuracoes/meus-dados" element={<SuspenseRoute><MeusDadosPage /></SuspenseRoute>} />
        <Route path="/configuracoes/notificacoes-e-preferencias" element={<SuspenseRoute><NotificacoesPreferenciasPage /></SuspenseRoute>} />
        <Route path="/configuracoes/configuracao-do-condominio" element={<SuspenseRoute><ConfiguracaoCondominioPage /></SuspenseRoute>} />
        <Route path="/configuracoes/permissoes-de-acesso" element={<SuspenseRoute><PermissoesAcessoPage /></SuspenseRoute>} />
        <Route path="/configuracoes/cadastros-de-usuario" element={<SuspenseRoute><CadastroPage /></SuspenseRoute>} />
        <Route path="/configuracoes/prestadores-servicos" element={<SuspenseRoute><PrestadoresServicosPage /></SuspenseRoute>} />
        <Route path="/configuracoes/areas-comuns" element={<Navigate to="/reservas/areas-comuns" replace />} />

        {/* Payment Agreement Details (specific route) */}
        {/* Legacy: keep details route if navigated directly, otherwise can be removed */}

        {/* Legacy routes for backward compatibility - remove after transition */}
        <Route path="/cadastro" element={<Navigate to="/configuracoes/cadastros-de-usuario" replace />} />
        <Route path="/agenda" element={<Navigate to="/agenda-sindico" replace />} />
        <Route path="/social/agenda-sindico" element={<Navigate to="/agenda-sindico" replace />} />
        <Route path="/suporte" element={<Navigate to="/social/suporte-tecnico" replace />} />
        <Route path="/reclamacoes" element={<Navigate to="/social/reclamacoes---sugestoes" replace />} />
        {/* Entrada legada: /manutencao-preventiva escolhe condomínio ou morador
            conforme a permissão, em vez de mandar todo mundo para o condomínio. */}
        <Route path="/manutencoes" element={<Navigate to="/manutencao-preventiva" replace />} />
        <Route path="/assembleias" element={<Navigate to="/social/assembleias" replace />} />
        <Route path="/assistencias" element={<Navigate to="/prestadores-de-servicos/condominio/elevadores" replace />} />
        <Route path="/pertences" element={<Navigate to="/social/meus-pertences" replace />} />
        <Route path="/patrimonios" element={<Navigate to="/portaria-e-seguranca/patrimonios" replace />} />
        <Route path="/inadimplencia" element={<Navigate to="/dashboard" replace />} />

        {/* EAD Module */}
        <Route path="/ead" element={<SuspenseRoute><CourseListingPage /></SuspenseRoute>} />
        <Route path="/ead/:courseId" element={<SuspenseRoute><CourseDetailsPage /></SuspenseRoute>} />
        <Route path="/ead/:courseId/lessons/:lessonId" element={<SuspenseRoute><LessonPage /></SuspenseRoute>} />

        {/* Legacy routes that don't directly match sidebar structure */}
        <Route path="/portaria" element={<Navigate to="/portaria-e-seguranca/check-in-out" replace />} />
        <Route path="/portaria/visitantes" element={<Navigate to="/portaria-e-seguranca/cadastro-de-visitantes" replace />} />
        <Route path="/portaria/entregas" element={<Navigate to="/portaria-e-seguranca/controle-de-encomendas" replace />} />
        <Route path="/portaria/alertas" element={<Navigate to="/portaria-e-seguranca/ocorrencias-de-seguranca" replace />} />

        <Route path="/construtora" element={<Navigate to="/dashboard" replace />} />
        <Route path="/construtora/projetos" element={<Navigate to="/dashboard" replace />} />
        <Route path="/construtora/orcamentos" element={<Navigate to="/dashboard" replace />} />
        <Route path="/construtora/comunicacoes" element={<Navigate to="/comunicacoes/comunicados-e-avisos" replace />} />

        <Route path="/condominios" element={<Navigate to="/configuracoes/configuracao-do-condominio" replace />} />
        <Route path="/condominios/lista" element={<SuspenseRoute><CondominioListPage /></SuspenseRoute>} />
        <Route path="/condominios/novo" element={<SuspenseRoute><CondominioCreatePage /></SuspenseRoute>} />
        <Route path="/condominios/:id" element={<SuspenseRoute><CondominioDetailsPage /></SuspenseRoute>} />
        <Route path="/condominios/:id/editar" element={<SuspenseRoute><CondominioEditPage /></SuspenseRoute>} />

        {/* Gestão de Condomínios (Admin MEP & Síndico) */}
        <Route path="/gestao-condominios" element={<SuspenseRoute><GestaoCondominiosPage /></SuspenseRoute>} />
        <Route path="/gestao-condominios/admin" element={<SuspenseRoute><AdminGestaoCondominiosPage /></SuspenseRoute>} />
        <Route path="/gestao-condominios/meus" element={<SuspenseRoute><SyndicCondominiosPage /></SuspenseRoute>} />

        {/* Configurações Administrativas (Admin MEP only) */}
        <Route path="/configuracoes-administrativas" element={<SuspenseRoute><AdminConfiguracoesPage /></SuspenseRoute>} />
        <Route path="/configuracoes-administrativas/gestao-condominios" element={<SuspenseRoute><AdminGestaoCondominiosPage /></SuspenseRoute>} />
        <Route path="/configuracoes-administrativas/gestao-usuarios" element={<SuspenseRoute><AdminGestaoUsuariosPage /></SuspenseRoute>} />
        <Route path="/configuracoes-administrativas/governanca-global" element={<Navigate to="/governanca/governanca-global" replace />} />

        {/* Style Guide (Development Only) */}
        {import.meta.env.DEV && (
          <Route path="/style-guide" element={<SuspenseRoute><StyleGuidePage /></SuspenseRoute>} />
        )}
        
        {/* Redirect to Not Found if no path matches */}
        <Route path="*" element={<SuspenseRoute><NotFoundPage /></SuspenseRoute>} />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary
      level="page"
      enableRetry
      errorReporter={(error, errorInfo) => {
        Sentry.captureException(error, {
          contexts: {
            react: {
              componentStack: errorInfo.componentStack ?? undefined,
            },
          },
        });
      }}
    >
      <Router>
        <Routes>
          {/* PUBLIC ROUTES - Outside AuthProvider/EstablishmentProvider */}
          <Route path="/set-password" element={
            <Suspense fallback={<PageLoader />}>
              <SetPasswordPage />
            </Suspense>
          } />
          <Route path="/forgot-password" element={
            <Suspense fallback={<PageLoader />}>
              <ForgotPasswordPage />
            </Suspense>
          } />
          <Route path="/cadastro-publico/comercio-local" element={
            <Suspense fallback={<PageLoader />}>
              <PublicCommerceSignupPage />
            </Suspense>
          } />
          <Route path="/comercio-local/gerenciar" element={
            <Suspense fallback={<PageLoader />}>
              <PublicCommerceManagePage />
            </Suspense>
          } />

          {/* ALL OTHER ROUTES - Inside full context */}
          <Route path="/*" element={
            <AuthProvider>
              <EstablishmentProvider>
                <AlertDialogProvider>
                  <ConfirmDialogProvider>
                  <PromptDialogProvider>
                    <AppWithAuth />
                    <Toaster
                      position="top-right"
                      toastOptions={{
                        duration: 4000,
                        // Erros persistem até o usuário fechar no OK (combinado).
                        error: { duration: Infinity },
                        style: {
                          background: '#363636',
                          color: '#fff',
                        },
                      }}
                    >
                      {(t) => (
                        <ToastBar toast={t}>
                          {({ icon, message }) => (
                            <div className="flex items-center gap-2">
                              {icon}
                              {message}
                              {t.type === 'error' && (
                                <button
                                  type="button"
                                  aria-label="Fechar"
                                  onClick={() => toast.dismiss(t.id)}
                                  className="ml-1 shrink-0 rounded-md bg-white/20 px-3 py-1 text-sm font-semibold text-white transition-colors hover:bg-white/30"
                                >
                                  OK
                                </button>
                              )}
                            </div>
                          )}
                        </ToastBar>
                      )}
                    </Toaster>
                  </PromptDialogProvider>
                  </ConfirmDialogProvider>
                </AlertDialogProvider>
              </EstablishmentProvider>
            </AuthProvider>
          } />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
